import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {Store} from '@ngrx/store';
import {ResearchKeywordCreateDTO, ResearchKeywordDTO} from '../../dto/research-keywords';
import {
  createResearchKeyword,
  deleteResearchKeyword,
  updateResearchKeyword
} from '../../services/research-keyword/research-keyword.actions';

import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatChipEditedEvent, MatChipInputEvent, MatChipsModule} from '@angular/material/chips';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {ResearchKeywordService} from '../../services/research-keyword/research-keyword.service';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {CloseableDialogComponent} from '../../../../utils/components/closeable-dialog/closeable-dialog.component';
import {ConfirmDialogComponent} from '../../../../utils/components/confirm-dialog/confirm-dialog.component';
import {ConfirmDialog} from '../../../../utils/models/confirm-dialog';
import {selectSettingByProjectId} from '../../services/screening-settings/screening-settings.selectors';
import {ScreeningSettingsResearchDBTypes} from '../../dto/screening-settings';
import {firstValueFrom} from 'rxjs';

interface KeywordDialogComponentData {
  keyword?: ResearchKeywordDTO;
  projectId: number;
}

@Component({
  selector: 'app-keyword-dialog',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatChipsModule,
    MatButtonModule,
    MatProgressBarModule,
    MatDialogModule,
    CloseableDialogComponent
],
  templateUrl: './keyword-dialog.component.html',
  styleUrl: './keyword-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeywordDialogComponent implements OnInit {
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly store: Store = inject(Store);
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly keywordService: ResearchKeywordService = inject(ResearchKeywordService);
  private readonly dialogRef: MatDialogRef<KeywordDialogComponent> = inject(MatDialogRef);
  private readonly dialog: MatDialog = inject(MatDialog);
  readonly data: KeywordDialogComponentData = inject<KeywordDialogComponentData>(MAT_DIALOG_DATA);
  readonly setting = this.store.selectSignal(selectSettingByProjectId(this.data.projectId));
  readonly addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  loadingSynonyms = false;

  form: FormGroup = this.fb.group({
    keyword: [this.data?.keyword?.keyword || '', Validators.required],
  });

  ngOnInit() {
    if (!this.data.keyword) {
      this.data.keyword = {keyword: "", synonyms: []} as any;
    } else {
      this.data.keyword = {...this.data.keyword, synonyms: [...this.data.keyword.synonyms]};
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  async save(): Promise<void> {
    if (!this.data.projectId) {
      return;
    }
    const updateLinkedQueries = await this.confirmLinkedQueryUpdate();
    if (updateLinkedQueries === null) {
      return;
    }
    if (this.data.keyword && this.data.keyword.id) {
      this.update(updateLinkedQueries);
    } else {
      this.create(updateLinkedQueries);
    }
    this.dialogRef.close(true);
  }

  async delete(): Promise<void> {
    if (this.data.keyword && this.data.keyword.id) {
      const updateLinkedQueries = await this.confirmLinkedQueryUpdate();
      if (updateLinkedQueries === null) {
        return;
      }
      this.store.dispatch(
        deleteResearchKeyword({
          projectId: this.data.projectId,
          id: this.data.keyword.id,
          updateLinkedQueries,
        })
      );
    }
    this.dialogRef.close(true);
  }

  addSynonym(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.data.keyword!.synonyms.push(value);
    }
    event.chipInput!.clear();
    this.cdr.detectChanges();
  }

  loadSynonyms(): void {
    const toCreate: ResearchKeywordCreateDTO = this.form.value;
    if (this.data.keyword?.synonyms && this.data.keyword?.synonyms.length > 0) {
      return;
    }
    if (toCreate.keyword && (!toCreate.synonyms || toCreate.synonyms.length === 0)) {
      this.loadingSynonyms = true;
      this.cdr.detectChanges();
      this.keywordService.getSynonyms(this.data.projectId, toCreate.keyword).subscribe(synonyms => {
        this.data.keyword!.synonyms = synonyms;
        this.loadingSynonyms = false;
        this.cdr.detectChanges();
      });
    }
  }

  removeSynonym(synonym: string): void {
    const index = this.data.keyword!.synonyms.indexOf(synonym);
    if (index >= 0) {
      this.data.keyword = {
        ...this.data.keyword,
        synonyms: [
          ...this.data.keyword!.synonyms.slice(0, index),
          ...this.data.keyword!.synonyms.slice(index + 1)
        ]
      } as ResearchKeywordDTO;
    }
    this.cdr.detectChanges();
  }

  editSynonym(synonym: string, event: MatChipEditedEvent) {
    const value = event.value.trim();

    if (!value) {
      this.removeSynonym(synonym);
      return;
    }
    const index = this.data.keyword!.synonyms.indexOf(synonym);
    if (index >= 0) {
      this.data.keyword!.synonyms[index] = value;
    }
    this.cdr.detectChanges();
  }

  private async confirmLinkedQueryUpdate(): Promise<boolean | null> {
    const setting = this.setting();
    const hasGenerated = setting?.researchDBSettings?.some(
      q => q.queryType === ScreeningSettingsResearchDBTypes.GENERATED
    ) ?? false;

    if (!hasGenerated) {
      return true;
    }

    const dialogData: ConfirmDialog = {
      title: 'Generated queries exist',
      message: 'You have generated queries built from your keywords. Do you want to regenerate them based on the updated keywords?',
      confirmButtonText: 'Regenerate queries',
      dismissButtonText: 'Keep unchanged',
    };

    const result = await firstValueFrom(
      this.dialog.open(ConfirmDialogComponent, {data: dialogData, width: '450px'}).afterClosed()
    );

    if (result === undefined) {
      return null;
    }
    return result === true;
  }

  private create(updateLinkedQueries: boolean): void {
    const toCreate: ResearchKeywordCreateDTO = this.form.value;
    toCreate.projectId = this.data.projectId;
    toCreate.synonyms = this.data.keyword!.synonyms;
    this.store.dispatch(
      createResearchKeyword({
        projectId: this.data.projectId,
        keyword: toCreate,
        updateLinkedQueries,
      })
    );
  }

  private update(updateLinkedQueries: boolean): void {
    const toUpdate: ResearchKeywordDTO = {...this.data.keyword, ...this.form.value};
    this.store.dispatch(
      updateResearchKeyword({
        id: toUpdate.id,
        projectId: this.data.projectId,
        keyword: toUpdate,
        updateLinkedQueries,
      })
    );
  }
}
